import { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import type { TestimonialDto } from '@portfolio/shared';
import { cn } from '@/lib/utils';
import { Highlight, plainText } from '../Highlight';
import { Reveal } from '../Reveal';
import { SwipeTrack } from '../SwipeTrack';
import { TestimonialFeature } from '../TestimonialFeature';
import { useLocale } from '../i18n';

export function TestimonialCard({ t }: { t: TestimonialDto }) {
  const [expanded, setExpanded] = useState(false);
  const { t: dict, to } = useLocale();
  const long = t.quote.length > 320;
  return (
    <figure className="flex h-full flex-col rounded-[24px] border border-line bg-card p-7 md:p-9">
      <span aria-hidden className="text-6xl leading-[0.5] font-black text-primary">
        “
      </span>
      <blockquote
        className={cn(
          'mt-6 text-body whitespace-pre-line text-ink-soft',
          long && !expanded && 'line-clamp-6',
        )}
      >
        {t.quote}
      </blockquote>
      {long && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 self-start text-caption font-medium text-primary-deep underline underline-offset-4"
          aria-expanded={expanded}
        >
          {expanded ? dict.showLess : dict.readMore}
        </button>
      )}
      <figcaption className="mt-auto flex items-center gap-3 pt-8">
        {t.avatar ? (
          <img
            src={t.avatar.thumbUrl ?? t.avatar.url}
            alt=""
            loading="lazy"
            className="size-12 rounded-full object-cover"
          />
        ) : (
          <span
            aria-hidden
            className="flex size-12 items-center justify-center rounded-full bg-primary-soft text-lg font-bold text-primary-deep"
          >
            {t.clientName.charAt(0)}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{t.clientName}</p>
          <p className="truncate text-caption text-muted">
            {[t.clientRole, t.company].filter(Boolean).join(', ')}
            {t.project && (
              <>
                {' · '}
                <Link
                  to={to(`/work/${t.project.slug}`)}
                  className="underline underline-offset-2 hover:text-primary"
                >
                  {t.project.title}
                </Link>
              </>
            )}
          </p>
        </div>
        {t.logo && (
          <img
            src={t.logo.url}
            alt={t.company ?? ''}
            loading="lazy"
            className="h-7 w-auto max-w-[90px] object-contain opacity-70"
          />
        )}
      </figcaption>
    </figure>
  );
}

const AUTOPLAY_MS = 8000;

/**
 * "Their Words Tell the Real Story." — one large testimonial at a time on the dark band, with a
 * segmented progress bar underneath. Drag or swipe the card to move between testimonials; it also
 * advances on its own (paused while hovered, focused, dragged, or for reduced-motion users), and
 * each segment jumps to its testimonial.
 */
export function Testimonials({ heading, items }: { heading: string; items: TestimonialDto[] }) {
  const [active, setActive] = useState(0);
  const { t: dict } = useLocale();
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  const reduce = useReducedMotion();
  const count = items.length;

  useEffect(() => {
    if (count < 2 || hovered || dragging || reduce) return;
    const id = setTimeout(() => setActive((i) => (i + 1) % count), AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [active, count, hovered, dragging, reduce]);

  if (!count) return null;

  return (
    <section
      id="testimonials"
      aria-roledescription="carousel"
      aria-label={plainText(heading)}
      className="container-site"
    >
      <Reveal>
        <h2 className="display-xl max-w-[7.3em] text-white">
          <Highlight text={heading} />
        </h2>
      </Reveal>

      <div
        className="mt-12 md:mt-[95px]"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
      >
        <SwipeTrack active={active} onChange={setActive} onDraggingChange={setDragging}>
          {items.map((t) => (
            <TestimonialFeature key={t.id} t={t} className="w-full" />
          ))}
        </SwipeTrack>

        {count > 1 && (
          <div
            role="tablist"
            aria-label={dict.chooseTestimonial}
            className="relative mt-[23px] flex gap-2.5"
          >
            {items.map((t, i) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={i === active}
                aria-label={dict.testimonialN(i + 1, t.clientName)}
                onClick={() => setActive(i)}
                className="group flex-1 py-2"
              >
                <span
                  className={cn(
                    'block h-[18px] rounded-full transition-[background-color,box-shadow] duration-500',
                    i === active
                      ? 'bg-primary shadow-[0_0_16px_2px_rgb(250_154_33/0.4)]'
                      : 'bg-black group-hover:bg-[#3a3a3a]',
                  )}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
